import * as React from "react";
import { useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { BarChart } from "@mui/x-charts/BarChart";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import { axiosApi } from "../../../interceptors/axios";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";

// TypeScript için interface tanımlamaları
type StatisticData = string[];

interface MonthlyData {
  month: string;
  count: number;
  growth: number;
  label: string;
}

// Tarih verilerini aylık olarak işleyen yardımcı fonksiyon
function processMonthlyData(dates: string[]): MonthlyData[] {
  // Tarihleri dayjs nesnelerine dönüştürme
  const dayjsDates = dates.map((dateStr) => dayjs(dateStr));

  // Tarihleri aylara göre gruplandırma
  const dataByMonth: Record<string, number> = {};

  dayjsDates.forEach((date) => {
    const monthKey = date.format("YYYY-MM");
    dataByMonth[monthKey] = (dataByMonth[monthKey] || 0) + 1;
  });

  // Son 12 ay için veri oluştur
  const today = dayjs();
  const monthlyData: MonthlyData[] = [];

  for (let i = 11; i >= 0; i--) {
    const currentMonth = today.subtract(i, "month");
    const monthKey = currentMonth.format("YYYY-MM");
    const previousMonthKey = currentMonth
      .subtract(1, "month")
      .format("YYYY-MM");

    const count = dataByMonth[monthKey] || 0;
    const previousCount = dataByMonth[previousMonthKey] || 0;

    // Büyüme yüzdesini hesapla
    let growth = 0;
    if (previousCount > 0) {
      growth = Math.round(((count - previousCount) / previousCount) * 100);
    } else if (count > 0) {
      growth = 100; // Önceki ay sıfırsa ve bu ay veri varsa, %100 artış
    }

    monthlyData.push({
      month: monthKey,
      count,
      growth,
      label: currentMonth.format("MMM YYYY"),
    });
  }

  return monthlyData;
}

export default function MonthlyGrowthChart(): JSX.Element {
  const theme = useTheme();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [monthlyData, setMonthlyData] = React.useState<MonthlyData[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  // API'den veri çekme
  React.useEffect(() => {
    const fetchStatistics = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await axiosApi.get<StatisticData>(
          "api/users/statistic"
        );

        // Tarih verilerini aylık olarak işle
        const processed = processMonthlyData(response.data);
        setMonthlyData(processed);

        setLoading(false);
      } catch (err) {
        console.error("İstatistik verileri alınırken hata:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Veriler yüklenirken bir hata oluştu"
        );
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  // Yükleme durumu
  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography>Veriler yükleniyor...</Typography>
        </CardContent>
      </Card>
    );
  }

  // Hata durumu
  if (error) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography color="error">Hata: {error}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Veri serilerini oluştur
  const chartData = {
    months: monthlyData.map((item) => item.label),
    counts: monthlyData.map((item) => item.count),
    growths: monthlyData.map((item) => item.growth),
  };

  // Son 6 ay gösterilecek şekilde filtrele
  const last6MonthsData = {
    months: chartData.months.slice(-6),
    counts: chartData.counts.slice(-6),
    growths: chartData.growths.slice(-6),
  };

  // Pozitif ve negatif büyüme değerlerini ayır (renklendirme için)
  const positiveGrowth = last6MonthsData.growths.map((growth) =>
    growth > 0 ? growth : 0
  );
  const negativeGrowth = last6MonthsData.growths.map((growth) =>
    growth < 0 ? growth : 0
  );

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography component="h2" variant="subtitle2">
            Aylık Kayıt Artışı
          </Typography>
          <Tooltip title="Son 6 ayın kayıt sayısı ve aylık artış oranlarını gösterir. Yeşil çubuklar artışı, kırmızı çubuklar düşüşü belirtir.">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Son 6 ayın kayıt oluşturma sayıları ve aylık değişim
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box>
              <BarChart
                dataset={last6MonthsData.months.map((month, index) => ({
                  month,
                  count: last6MonthsData.counts[index],
                  positiveGrowth: positiveGrowth[index],
                  negativeGrowth: negativeGrowth[index],
                }))}
                xAxis={[
                  {
                    scaleType: "band",
                    dataKey: "month",
                  },
                ]}
                series={[
                  {
                    dataKey: "count",
                    label: "Kayıt Sayısı",
                    valueFormatter: (value) => value?.toString() || "",
                    color: theme.palette.primary.main,
                  },
                ]}
                height={243}
                layout="vertical"
                margin={{ left: 80, right: 30, top: 50, bottom: 30 }}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
